import { useCallback, useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import type { ReactNode } from 'react'
import { IconButton } from './IconButton'
import styles from './Modal.module.css'

export interface ModalProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly title: string
  /** Accessible name for the close control — must be translated by the caller. */
  readonly closeLabel: string
  readonly wide?: boolean
  readonly footer?: ReactNode
  readonly children: ReactNode
}

/** Everything that can hold focus inside the dialog, in DOM order. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * The modal the brief calls out by name: the app this replaces ships a
 * 12-line `Modal.tsx` with no focus trap, no `role="dialog"` and no Escape
 * handler. Every one of those gaps is closed here, and each is covered by a
 * test in Modal.test.tsx, because a focus trap is precisely the kind of thing
 * that silently stops working and nobody notices with a mouse.
 *
 *  - `role="dialog"` + `aria-modal` + `aria-labelledby` → announced as a
 *    dialog, with its title, instead of as anonymous text.
 *  - Focus moves into the dialog on open and RETURNS to the element that
 *    opened it on close. Without the return, closing a modal drops keyboard
 *    users at the top of the document.
 *  - Tab and Shift+Tab cycle within the dialog.
 *  - Escape closes (WCAG 2.1.2 No Keyboard Trap).
 *  - Content behind is `inert`-equivalent via `aria-hidden` on the backdrop
 *    click target only; the backdrop itself is not focusable, so a screen
 *    reader cannot land on it.
 *
 * Rendered through a portal so an ancestor's `overflow: hidden` or stacking
 * context cannot clip it — a dialog that renders inside a scrolling table
 * container is the usual way this breaks.
 */
export function Modal({ open, onClose, title, closeLabel, wide = false, footer, children }: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const returnFocusTo = useRef<HTMLElement | null>(null)
  const titleId = useId()

  const focusables = useCallback((): HTMLElement[] => {
    const root = dialogRef.current
    return root ? Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)) : []
  }, [])

  // Remember the opener before the dialog steals focus, and restore on close.
  useEffect(() => {
    if (!open) return
    returnFocusTo.current = document.activeElement as HTMLElement | null
    const first = focusables()[0] ?? dialogRef.current
    first?.focus()
    return () => {
      returnFocusTo.current?.focus?.()
    }
  }, [open, focusables])

  // The page behind must not scroll while a modal is open.
  useEffect(() => {
    if (!open) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
        return
      }
      if (event.key !== 'Tab') return
      const items = focusables()
      if (items.length === 0) {
        event.preventDefault()
        return
      }
      const first = items[0]
      const last = items[items.length - 1]
      const active = document.activeElement
      // Wrap in both directions, and also catch the case where focus has
      // escaped the dialog entirely (a stray programmatic focus elsewhere).
      if (event.shiftKey && (active === first || !dialogRef.current?.contains(active))) {
        event.preventDefault()
        last?.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first?.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [open, onClose, focusables])

  if (!open) return null

  return createPortal(
    <div
      className={styles.backdrop}
      // Presentational, and accurately so: the backdrop carries no content and
      // is not a control. Click-to-dismiss is a redundant convenience — Escape
      // and the close button are the real affordances, and both are keyboard
      // reachable — so this needs no role, no tabindex and no key handler of
      // its own.
      role="presentation"
      // Only the backdrop itself dismisses: a click that started inside the
      // dialog and ended here (a drag while selecting text) must not close it.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={[styles.dialog, wide ? styles.wide : ''].filter(Boolean).join(' ')}
      >
        <div className={styles.header}>
          <h2 className={styles.title} id={titleId}>
            {title}
          </h2>
          <IconButton label={closeLabel} onClick={onClose} size="sm">
            ✕
          </IconButton>
        </div>
        <div className={styles.body}>{children}</div>
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>,
    document.body,
  )
}
